import jsPDF from "jspdf";
import "jspdf-autotable";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";
import dayjs from "dayjs";
import CurrencyFormat from "../../../../hook/CurrencyFormat";

// ─── helpers ────────────────────────────────────────────────
const str = (v) => (v === null || v === undefined ? "" : String(v));

const fmt = (v, cf) =>
  str(
    cf
      ? cf(v)
      : Number(v || 0).toLocaleString("th-TH", { minimumFractionDigits: 2 }),
  );

const fmtDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = dayjs(dateStr);
  const M = [
    "ม.ค.",
    "ก.พ.",
    "มี.ค.",
    "เม.ย.",
    "พ.ค.",
    "มิ.ย.",
    "ก.ค.",
    "ส.ค.",
    "ก.ย.",
    "ต.ค.",
    "พ.ย.",
    "ธ.ค.",
  ];
  return `${d.date()} ${M[d.month()]} ${d.year() + 543}`;
};

// ─── main export ────────────────────────────────────────────
export const generateAdvancePayReport = ({ dataList, fromDate, toDate }) => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const setF = (style = "normal", size = 13) => {
    pdf.setFont("THSarabunNew", style);
    pdf.setFontSize(size);
  };

  const pageW = 210;
  const pageH = pdf.internal.pageSize.getHeight();
  const mL = 5;
  const mR = 5;
  const cW = pageW - mL - mR;
  const now = dayjs().format("DD/MM/YYYY HH:mm:ss");
  const fFrom = fromDate ? dayjs(fromDate).format("DD/MM/YYYY") : "";
  const fTo = toDate ? dayjs(toDate).format("DD/MM/YYYY") : "";

  const cashGroup = dataList.filter((r) => r.CHKFLAG === "1");
  const transferGroup = dataList.filter((r) => r.CHKFLAG !== "1");

  let pageNum = 1;

  // ── Company Header ───────────────────────────────────────
  const drawCompanyHeader = () => {
    const steps = 40;
    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      pdf.setFillColor(
        Math.round(255 - (255 - 235) * t),
        Math.round(255 - (255 - 240) * t),
        255,
      );
      pdf.rect((pageW / steps) * i, 2, pageW / steps + 0.5, 22, "F");
    }
    setF("bold", 14);
    pdf.text("บริษัท ซีเอแอล ลิสซิ่ง จำกัด", pageW - mR, 8, { align: "right" });
    setF("normal", 10);
    pdf.setTextColor(80, 80, 80);
    pdf.text(
      "1/20-24 ถ.มิตรภาพ ต.ในเมือง อ.เมือง จ.ขอนแก่น 40000",
      pageW - mR,
      13,
      { align: "right" },
    );
    pdf.text("เลขประจำตัวผู้เสียภาษี", pageW - mR, 18, { align: "right" });
    pdf.setTextColor(0, 0, 0);
  };

  // ── Page Header ──────────────────────────────────────────
  const drawPageHeader = () => {
    drawCompanyHeader();
    setF("bold", 18);
    pdf.text("รายงานประจำวันตั้งเบิกทดรอง", mL, 30);
    setF("normal", 13);
    pdf.text(`จากวันที่ ${fFrom}  ถึงวันที่ ${fTo}`, mL, 36);
    setF("normal", 11);
    pdf.setTextColor(80, 80, 80);
    pdf.text(`วันที่พิมพ์ : ${now}`, mL, 42);
    pdf.text(`หน้าที่ : ${pageNum}`, pageW - mR, 44, { align: "right" });
    pdf.setTextColor(0, 0, 0);
    pdf.setDrawColor(0, 0, 180);
    pdf.setLineWidth(0.4);
    pdf.line(mL, 38, pageW - mR, 38);
  };

  // ── Section Title ────────────────────────────────────────
  const drawSectionTitle = (title, y) => {
    pdf.setFillColor(235, 240, 255);
    pdf.setDrawColor(0, 0, 180);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(mL, y, cW, 8, 1, 1, "FD");
    setF("bold", 14);
    pdf.setTextColor(0, 0, 180);
    pdf.text(title, mL + 3, y + 5.8);
    pdf.setTextColor(0, 0, 0);
    return y + 9;
  };

  // ── Table Border ─────────────────────────────────────────
  const drawTableBorder = (startY, endY) => {
    pdf.setDrawColor(0, 0, 180);
    pdf.setLineWidth(0.25);
    pdf.line(mL, startY, pageW - mR, startY); // บน
    pdf.line(mL, endY, pageW - mR, endY); // ล่าง
    pdf.line(mL, startY, mL, endY); // ซ้าย
    pdf.line(pageW - mR, startY, pageW - mR, endY); // ขวา
  };

  // ── Draw Section ─────────────────────────────────────────
  const drawSection = (list, title, startY) => {
    if (!list.length) return startY;

    let y = startY;

    if (y > 250) {
      pdf.addPage();
      pageNum++;
      drawPageHeader();
      y = 45; // ✅ ตรงกับ margin.top
    }

    y = drawSectionTitle(title, y);

    const body = list.map((r, i) => {
      const nameText = r.NAME_LAWYER
        ? `${r.NAME_LAWYER}${r.OFFCODE ? ` (${r.OFFCODE})` : ""}`
        : r.USERID || "-";
      return [
        `${i + 1}.`,
        str(fmtDate(r.DOCDT)),
        str(r.DOCNO || "-"),
        str(r.MEMO1 || "-"),
        str(nameText),
        currencyFormatPoint(r.TOTAMT ?? r.AMOUNT ?? 0),
        str(r.FLAG ?? "N"),
      ];
    });

    const total = list.reduce(
      (s, r) => s + parseFloat(r.TOTAMT ?? r.AMOUNT ?? 0),
      0,
    );
    body.push(["", "", "", "", "รวม", currencyFormatPoint(total), ""]);

    const tableStartY = y;
    const pageInfo = {};

    pdf.autoTable({
      head: [
        [
          "No.",
          "วันที่เอกสาร",
          "เลขที่เอกสาร",
          "รายละเอียดการจ่าย",
          "ผู้เบิก",
          "จำนวนเงิน",
          "สถานะ*",
        ],
      ],
      body,
      startY: y,
      styles: {
        font: "THSarabunNew",
        fontSize: 13,
        cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
        lineWidth: 0,
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [240, 235, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 13,
        halign: "center",
        lineWidth: 0,
      },
      bodyStyles: { minCellHeight: 8, lineWidth: 0 },
      tableLineWidth: 0,
      columnStyles: {
        0: { halign: "center", cellWidth: 12 },
        1: { halign: "center", cellWidth: 24 },
        2: { halign: "center", cellWidth: 25 },
        3: { halign: "left", cellWidth: 60 },
        4: { halign: "left", cellWidth: 35 },
        5: { halign: "right", cellWidth: 25 },
        6: { halign: "center", cellWidth: 18 },
      },
      margin: { left: mL, right: mR, top: 45 }, // ✅ แก้ไข: เพิ่ม top: 45
      didParseCell(data) {
        const isTotal = data.row.index === body.length - 1;
        if (data.section === "body") {
          if (isTotal) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [240, 235, 255];
          }
          if (data.column.index === 6 && !isTotal) {
            data.cell.styles.textColor =
              data.cell.raw === "Y" ? [0, 0, 0] : [220, 0, 0];
          }
        }
      },
      didDrawCell(data) {
        const isTotal = data.row.index === body.length - 1;
        if (data.section === "body" && isTotal) {
          pdf.setDrawColor(0, 0, 180);
          pdf.setLineWidth(0.25);
          pdf.line(
            data.cell.x,
            data.cell.y,
            data.cell.x + data.cell.width,
            data.cell.y,
          );
        }
      },
      didDrawPage(data) {
        const pn = pdf.internal.getCurrentPageInfo().pageNumber;
        const sY = pn === 1 ? tableStartY : data.settings.margin.top;
        pageInfo[pn] = { sY };

        if (pn > pageNum) {
          pageNum = pn;
          drawPageHeader();
        }
      },
    });

    // ✅ วาดกรอบทุกหน้า หลัง autoTable เสร็จ
    const finalY = pdf.lastAutoTable.finalY;
    const totalPages = pdf.internal.getNumberOfPages();

    Object.entries(pageInfo).forEach(([pnStr, { sY }]) => {
      const pn = parseInt(pnStr);
      const isLast = pn === totalPages;
      const eY = isLast ? finalY : pageH - 10;

      pdf.setPage(pn);
      pdf.setDrawColor(0, 0, 180);
      pdf.setLineWidth(0.25);
      pdf.line(mL, sY, pageW - mR, sY); // บน
      pdf.line(mL, sY, mL, eY); // ซ้าย
      pdf.line(pageW - mR, sY, pageW - mR, eY); // ขวา
      pdf.line(mL, eY, pageW - mR, eY); // ล่าง
    });

    // ✅ กลับมาหน้าสุดท้ายเสมอ
    pdf.setPage(totalPages);

    return finalY + 6;
  };

  // ── Render ───────────────────────────────────────────────
  drawPageHeader();

  let y = 45;
  y = drawSection(cashGroup, "เบิกโดย : เงินสด", y);
  y = drawSection(transferGroup, "เบิกโดย : รอเคลียร์ - เงินโอน", y);

  // ── ยอดรวมทั้งหมด ────────────────────────────────────────
  const totalAll = dataList.reduce(
    (s, r) => s + parseFloat(r.TOTAMT ?? r.AMOUNT ?? 0),
    0,
  );

  pdf.autoTable({
    body: [
      [
        {
          content: "* N รอเคลียร์เงิน , Y เคลียร์เงินแล้ว",
          styles: {
            fontSize: 16,
            halign: "left",
            fontStyle: "bold",
            textColor: [0, 0, 180],
          },
        },
        {
          content: `รวมยอดเบิก     ${currencyFormatPoint(totalAll)}`,
          styles: {
            fontSize: 16,
            halign: "right",
            fontStyle: "bold",
            textColor: [0, 0, 180],
          },
        },
      ],
    ],
    startY: y,
    bodyStyles: { minCellHeight: 8, lineWidth: 0 },
    styles: {
      font: "THSarabunNew",
      fontSize: 12,
      cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
      lineColor: [0, 0, 180],
      textColor: [0, 0, 0],
    },
    tableLineWidth: 0.25,
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 55 },
    },
    margin: { left: mL, right: mR },
    didParseCell(data) {
      data.cell.styles.fillColor = [240, 235, 255];
    },
    didDrawCell(data) {
      if (data.section === "body") {
        pdf.setDrawColor(0, 0, 180);
        pdf.setLineWidth(0.4);
        const { x, y: cy, width, height } = data.cell;
        const isFirst = data.column.index === 0;
        const isLast = data.column.index === data.table.columns.length - 1;
        pdf.line(x, cy, x + width, cy);
        pdf.line(x, cy + height, x + width, cy + height);
        if (isFirst) pdf.line(x, cy, x, cy + height);
        if (isLast) pdf.line(x + width, cy, x + width, cy + height);
      }
    },
  });

  // ── เปิด PDF ─────────────────────────────────────────────
  const blob = pdf.output("blob");
  const url = URL.createObjectURL(blob);
  const win = window.open(url);
  if (win) win.onload = () => win.print();
  else alert("กรุณาปิดการบล็อกป๊อปอัปเพื่อใช้งานฟังก์ชันนี้");
};
